import React from 'react';
import BaseGrid from 'components/atoms/base-grid';
import BaseTextArea from 'components/atoms/base-textarea';
import BaseInput from 'components/atoms/base-input';
import classNames from 'classnames';
import ChainIcon from 'components/atoms/icons/chainIcon';
import CopyIcon from 'components/atoms/icons/copyIcon';
import { copyTextWithResult } from 'utils/helpers/copy-text-with-result';
import { cropText } from 'infrastructure/functions';
import { useTranslation } from 'react-i18next';
import BaseTimePicker from 'components/atoms/base-timepicker';
import { DateFormats } from 'infrastructure/enums';

import DoctorAndAgencyDoctorSelectBox from '../all-doctor-select-box';
import AppointmentMethodSelectBox from '../appointment-method-select-box';
import s from './styles.module.scss';
import CheckboxItem from './checkboxItem';
import UncheckableRadioItem from './uncheckableRadioItem';
import InputNumberItem from './input-number-item';
import DateItem from './dateItem';

import type { IFormItemExtraProps } from './types';

const FormItemExtra: React.FC<IFormItemExtraProps> = (props) => {
  const {
    formik,
    type,
    name: generalName = '',
    options,
    label = '',
    divider,
    formItemName = '',
    children,
    isChild,
    link,
    patientGuid,
  } = props;
  const { t } = useTranslation();

  const wrapperClassNames = classNames(s.body, {
    [s['extra-body']]: isChild ? false : type !== 'inputNumber',
  });

  return (
    <div className={wrapperClassNames}>
      {type === 'checkbox' &&
        options &&
        (label ? (
          <>
            <div className={s.header}>
              <div className={s.title}>{label}</div>
            </div>
            {options.map((option) => (
              <CheckboxItem
                option={option}
                formik={formik}
                generalName={generalName}
                key={option.key}
              />
            ))}
          </>
        ) : (
          <div
            className={classNames(s['not-escalated'], {
              [s['is-child']]: isChild,
            })}
          >
            {options.map((option) => (
              <CheckboxItem
                option={option}
                formik={formik}
                generalName={generalName}
                key={option.key}
              />
            ))}
          </div>
        ))}
      {type === 'datepicker' && options && (
        <BaseGrid columns={2} className={s['item-row']}>
          {options.map((option) => {
            return (
              <DateItem option={option} formik={formik} key={option.key} />
            );
          })}
        </BaseGrid>
      )}

      {type === 'text' && (
        <>
          {label && <label className={s.label}>{label}</label>}
          <BaseTextArea
            rows={6}
            placeholder="Type here details"
            onChange={(value) => formik.setFieldValue(generalName, value)}
            className={s['extra-text']}
            value={formik.values[generalName]}
          />
          {generalName === 'medicationChangesFreeText' && (
            <span className={classNames(s.info, s.note)}>
              {t('messages.medicationIssues')}
            </span>
          )}
        </>
      )}
      {type === 'link' && link && (
        <div className={s.link}>
          <div>
            <ChainIcon fill="#0041C2" />
            <a href={link} target="_blank">
              {cropText(link, 36)}
            </a>
          </div>
          <button onClick={() => copyTextWithResult(link)}>
            <CopyIcon />
            <span>{t('controls.copyLink')}</span>
          </button>
        </div>
      )}

      {type === 'radio' && (
        <>
          {divider ? (
            <div className={s.header}>
              <div className={s.title}>{label}</div>
            </div>
          ) : (
            <label>{label}</label>
          )}
          <UncheckableRadioItem
            options={options ?? []}
            name={generalName}
            value={formik.values[generalName]}
            formik={formik}
          />
        </>
      )}

      {type === 'doubleRowInput' && options && (
        <BaseGrid columns={2} className={s['item-row']}>
          {options.map((option) => (
            <div key={option.key}>
              <label className={s.label}>
                <span>{option.label}</span>
              </label>
              <BaseInput
                onChange={(value) => {
                  formik.setFieldValue(option.value as string, value);
                }}
                value={formik.values[option.value as string]}
                disabled={formik.values[formItemName] !== null}
              />
            </div>
          ))}
        </BaseGrid>
      )}

      {type === 'input' && options && (
        <div className={s['extra-input']}>
          {options.map((option) => (
            <React.Fragment key={option.key}>
              <label className={s.label}>
                <span>{option.label}</span>
              </label>
              <BaseInput
                disabled={option?.disabled}
                onChange={(value) => formik.setFieldValue(generalName, value)}
                value={formik.values[option.value as string]}
                invalid={Boolean(formik.errors[option.value as string])}
              />
            </React.Fragment>
          ))}
        </div>
      )}

      {type === 'inputNumber' && options && (
        <div className={s['extra-num']}>
          {options.map((option) => (
            <InputNumberItem option={option} formik={formik} />
          ))}
        </div>
      )}

      {type === 'labelCheckbox' && options && (
        <div className={s['extra-label']}>
          {divider && <div className={s['extra-label-divider']} />}
          <label className={s.label}>
            <span>{label}</span>
          </label>
          <div>
            {options.map((option) => (
              <CheckboxItem
                option={option}
                formik={formik}
                generalName={generalName}
                key={option.key}
              />
            ))}
          </div>
        </div>
      )}

      {type === 'doctor-and-agencydoctor-select-box' &&
        patientGuid &&
        options && (
          <>
            {options[0].label && (
              <label className={s.label}>{options[0].label}</label>
            )}
            <BaseGrid columns={1} className={s['appointment-item']}>
              <DoctorAndAgencyDoctorSelectBox
                value={
                  formik.values?.recentUpcomingAppointmentsProviderGuid ?? ''
                }
                patientGuid={patientGuid}
                onChange={(value, option) => {
                  formik.setFieldValue(
                    'recentUpcomingAppointmentsProviderGuid',
                    value,
                  );
                  formik.setFieldValue(
                    'recentUpcomingAppointmentsProvider',
                    option.record?.label,
                  );
                  formik.setFieldValue(
                    'recentUpcomingAppointmentsProviderType',
                    option.record?.type,
                  );
                }}
              />
            </BaseGrid>
          </>
        )}

      {type === 'time' && options && (
        <>
          {options[0].label && (
            <label className={s.label}>{options[0].label}</label>
          )}
          <BaseGrid columns={2} className={s['appointment-item']}>
            <BaseTimePicker
              value={formik.values.recentUpcomingAppointmentsTime}
              format={DateFormats['HH:mm']}
              onChange={(value) => {
                formik.setFieldValue('recentUpcomingAppointmentsTime', value);
              }}
            />
          </BaseGrid>
        </>
      )}

      {type === 'appointment-method-select-box' && patientGuid && options && (
        <>
          {options[0].label && (
            <label className={s.label}>{options[0].label}</label>
          )}
          <BaseGrid columns={1} className={s['appointment-item']}>
            <AppointmentMethodSelectBox
              value={formik.values.recentUpcomingAppointmentsMethod}
              onChange={(value) => {
                formik.setFieldValue('recentUpcomingAppointmentsMethod', value);
              }}
            />
          </BaseGrid>
        </>
      )}

      {children}
    </div>
  );
};

export default FormItemExtra;
